import React, { useContext, createContext, useState, useEffect } from "react";
import { notification } from "antd";
import {
  CustomerCareDetails,
  DefaultCustomerCareDetails,
  caregiverSummary,
  defaultCaregiverSummary,
} from "../types/PersonTypes";
import {
  GetRegistrationStatusDetails,
  getPersonSummary,
  getPersonUserAccountInfo,
  getWandaCustomerCareDetails,
} from "../services";
import {
  getUserRole,
  isAuthenticated,
} from "../services/AuthService/AuthService";
import {
  GeneralDefinition,
  Permissions,
  SearchPattern,
} from "../types/ApplicationTypes";
import { IServiceUserInfo } from "../types/ServiceUserInfo";
import { getImageUrl } from "../helpers/personHelpers";

export const PersonSummaryContext = createContext<any | undefined>(undefined);

export const usePersonSummaryCtx = () => useContext(PersonSummaryContext);

export const PersonSummaryProvider: React.FC = ({ children }) => {
  const [caregiverSummaryInfo, setCaregiverSummaryInfo] = useState<
    caregiverSummary
  >(defaultCaregiverSummary);
  const [serviceUsers, setServiceUsers] = useState<IServiceUserInfo[]>();
  const [isWandaConcierge, setIsWandaConcierge] = useState<boolean>(false);
  const [isLoadingPersonData, setIsLoadingPersonData] = useState<boolean>(true);
  const [customerCareDetails, setCustomerCareDetails] = useState<
    CustomerCareDetails
  >(DefaultCustomerCareDetails);

  useEffect(() => {
    const getAuthenticatedData = async () => {
      const isUserAuthenticated = await isAuthenticated();
      const loginRoles: string[] = await getUserRole();
      if (
        isUserAuthenticated &&
        loginRoles.includes(Permissions.ROLE_WANDACONCIERGE)
      ) {
        const personId = Number(
          sessionStorage.getItem("POST_LOGIN_URL_PATH_KEY")
        );
        getPersonData(personId);
      } else if (
        isUserAuthenticated &&
        !loginRoles.includes(Permissions.ROLE_WANDACONCIERGE)
      ) {
        getPersonData();
      }
    };
    getAuthenticatedData();
  }, []);

  const getPersonData = async (personId?: number) => {
    try {
      const personData = await getPersonSummary(personId);
      const accountInfo = await getPersonUserAccountInfo(
        personData.personInfo.personID
      );
      const { data: imageKey } = await GetRegistrationStatusDetails(
        accountInfo.userName,
        personData.personInfo.personID,
        SearchPattern.UploadImageId,
        GeneralDefinition.EmptyString
      );
      let imageUrl;
      if (imageKey) {
        imageUrl = await getImageUrl(imageKey);
      }

      if (personData.personInfo.personID) {
        const data: caregiverSummary = {
          personId: personData.personInfo.personID,
          firstName: personData.personInfo?.firstName,
          lastName: personData.personInfo?.lastName,
          dateOfBirth: personData.personInfo?.dateOfBirth,
          gender: personData.personInfo?.gender,
          imageKey,
          imageUrl,
          age: personData.personInfo?.age,
          primaryLanguage: personData.personInfo?.primaryLanguage,
          wandaEmail: accountInfo.userName,
          wandaEmailAddressAttribute:
            personData.personContact?.wandaEmailAddressAttribute,
          primaryPhone: personData.personContact?.primaryPhone,
          primaryPhoneAttribute:
            personData.personContact?.primaryPhoneAttribute,
          homeAddress: personData.personAddresses.homeAddress,
          mailingAddress: personData.personAddresses.mailingAddress,
        };
        setCaregiverSummaryInfo(data);
        const loginRoles: string[] = await getUserRole();
        if (
          loginRoles &&
          loginRoles.includes(Permissions.ROLE_WANDACONCIERGE)
        ) {
          setIsWandaConcierge(true);
        }
        handleContactUsInfo();
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred during fetching the Caregiver info.",
      });
    } finally {
      setIsLoadingPersonData(false);
    }
  };

  const handleContactUsInfo = async () => {
    try {
      const details = await getWandaCustomerCareDetails("");
      setCustomerCareDetails(details);
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Failed to fetch Customer care details. Please try again later.",
      });
    }
  };

  return (
    <PersonSummaryContext.Provider
      value={{
        caregiverSummaryInfo,
        setCaregiverSummaryInfo,
        isWandaConcierge,
        isLoadingPersonData,
        getPersonData,
        serviceUsersCtx: [serviceUsers, setServiceUsers],
        customerCareDetails,
      }}
    >
      {children}
    </PersonSummaryContext.Provider>
  );
};
